import type { CssKeyframe } from './pose';
import { contrast, readable, shade, withAlpha, type MotionPalette } from './palette';
import type { Label, PresetParam, PresetParams } from './presets';
import type { Box } from './transitions';
import {
  BACK,
  IN,
  IO,
  OUT,
  P_COLOR,
  P_COLOR2,
  P_COLOR3,
  P_COUNT,
  P_DIR,
  P_MODE,
  P_OPACITY,
  P_TARGET,
  P_TEXT,
  P_X,
  P_Y,
  balancedLines,
  color,
  coverAnim,
  dirOf,
  fitFont,
  full,
  headlineWords,
  lbl,
  num,
  off,
  palette3,
  px,
  r2,
  rng,
  str,
  unit,
  type Dir,
  P_FONT,
  P_HIDE_SOURCE,
  P_SOURCE,
  P_WEIGHT,
} from './fx-kit';
import { REEL_FX_KINDS } from './fx-reel';
import { ORIGINAL_FX_KINDS } from './fx-original';

export { rng };

/**
 * Motion graphics generated on top of (or under) the design: cover transitions (panels,
 * shutters, a circle), accents (rings, bursts, confetti, light sweeps, a marker underline),
 * ambient backdrops (aurora, grid, floating shapes, a marquee) and a title card for openers.
 *
 * A kind builds a small tree of absolutely positioned nodes with Web Animations keyframes, in
 * canvas pixels, from the item's parameters and the template's palette. It is pure data, so the
 * editor preview and the video renderer draw exactly the same frames, and the output is fully
 * deterministic (seeded randomness).
 */

export interface FxAnim {
  keyframes: CssKeyframe[];
  /** ms from the start of the fx item. */
  delay: number;
  duration: number;
  easing?: string;
  iterations?: number;
  fill?: 'none' | 'forwards' | 'backwards' | 'both';
  /** Animate the node's path (stroke drawing) instead of the node itself. */
  target?: 'path';
}

export interface FxPath {
  /** In canvas px: the node spans the canvas. */
  d: string;
  stroke: string;
  width: number;
  fill?: string;
  cap?: 'round' | 'butt' | 'square';
}

export interface FxNode {
  /** CSS properties (camelCase) with units. Top-level nodes are absolutely positioned. */
  style: Record<string, string | number>;
  text?: string;
  path?: FxPath;
  children?: FxNode[];
  anims?: FxAnim[];
  /**
   * Text box: the player shrinks the font size until every child row fits (layout is measured
   * before any animation applies).
   */
  fit?: boolean;
}

export interface FxFont {
  family: string;
  weight: number;
  style: string;
  transform: string;
}

export interface FxContext {
  canvas: { width: number; height: number };
  palette: MotionPalette;
  params: PresetParams;
  duration: number;
  seed: number;
  /** Headline of the template (title cards, marquees). */
  text: string;
  /** All visible text of the template in reading order, one layer per line (captions). */
  copy?: string;
  font: FxFont;
  /** Box of the target layer (underline, sweep), canvas px. */
  box?: Box;
}

export type FxCategory = 'cover' | 'accent' | 'ambient' | 'text' | 'interface' | 'pattern';

export interface FxKind {
  id: string;
  label: Label;
  category: FxCategory;
  z: 'front' | 'back';
  duration: number;
  params: PresetParam[];
  build: (ctx: FxContext) => FxNode[];
}

// ---------------------------------------------------------------- covers

const panels: FxKind = {
  id: 'panels',
  label: lbl(
    'Colour panels',
    'Кольорові панелі',
    'Kolorowe panele',
    'Panouri colorate',
    'Paneles de color',
    'Цветные панели',
  ),
  category: 'cover',
  z: 'front',
  duration: 1200,
  params: [
    P_MODE,
    P_DIR,
    P_COUNT(3, 1, 6),
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
    P_COLOR3('dark'),
  ],
  build(ctx) {
    const d = dirOf(ctx);
    const n = Math.round(Math.max(1, Math.min(6, num(ctx, 'count', 3))));
    const colors = palette3(ctx, 'accent', 'accent2', 'dark');
    return Array.from({ length: n }, (_, i) => ({
      style: full(ctx, { background: colors[i % 3], willChange: 'transform' }),
      // The top panel leaves first and uncovers the ones under it, like peeling layers
      anims: [
        coverAnim(
          ctx,
          i,
          n,
          n - 1 - i,
          { transform: off(d, -1) },
          { transform: 'translate(0%, 0%)' },
          { transform: off(d, 1) },
        ),
      ],
    }));
  },
};

const diagonal: FxKind = {
  id: 'diagonal',
  label: lbl('Diagonal sweep', 'Діагональ', 'Ukos', 'Diagonală', 'Diagonal', 'Диагональ'),
  category: 'cover',
  z: 'front',
  duration: 1100,
  params: [
    P_MODE,
    { ...P_DIR, default: 'right' },
    P_COUNT(2, 1, 4),
    P_COLOR('color', 'accent'),
    P_COLOR2('dark'),
    P_COLOR3('accent2'),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const d = dirOf(ctx) === 'left' ? -1 : 1;
    const n = Math.round(Math.max(1, Math.min(4, num(ctx, 'count', 2))));
    const colors = palette3(ctx, 'accent', 'dark', 'accent2');
    const skew = 22;
    const extra = H * Math.tan((skew * Math.PI) / 180);
    const w = W + extra * 2;
    const t = (k: number) => `translateX(${px(k * (w + 2))}) skewX(${-skew * d}deg)`;
    return Array.from({ length: n }, (_, i) => ({
      style: {
        position: 'absolute',
        left: px(-extra),
        top: '0px',
        width: px(w),
        height: px(H),
        background: colors[i % 3],
      },
      anims: [
        coverAnim(
          ctx,
          i,
          n,
          n - 1 - i,
          { transform: t(-d) },
          { transform: t(0) },
          { transform: t(d) },
          0.2,
        ),
      ],
    }));
  },
};

const shutter: FxKind = {
  id: 'shutter',
  label: lbl('Shutter', 'Жалюзі', 'Żaluzje', 'Jaluzele', 'Persiana', 'Жалюзи'),
  category: 'cover',
  z: 'front',
  duration: 1200,
  params: [
    P_MODE,
    { ...P_DIR, default: 'down' },
    P_COUNT(6, 2, 16),
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const d = dirOf(ctx, 'down');
    const n = Math.round(Math.max(2, Math.min(16, num(ctx, 'count', 6))));
    const c1 = color(ctx, 'color', 'accent');
    const c2 = color(ctx, 'color2', 'accent2');
    const vertical = d === 'down' || d === 'up';
    return Array.from({ length: n }, (_, i) => {
      const size = (vertical ? W : H) / n;
      const style = vertical
        ? {
            position: 'absolute',
            left: px(i * size - 0.5),
            top: '0px',
            width: px(size + 1),
            height: px(H),
          }
        : {
            position: 'absolute',
            top: px(i * size - 0.5),
            left: '0px',
            height: px(size + 1),
            width: px(W),
          };
      return {
        style: { ...style, background: i % 2 ? c2 : c1 },
        anims: [
          coverAnim(
            ctx,
            i,
            n,
            i,
            { transform: off(d, -1) },
            { transform: 'translate(0%, 0%)' },
            { transform: off(d, 1) },
            0.1,
          ),
        ],
      };
    });
  },
};

const slices: FxKind = {
  id: 'slices',
  label: lbl('Slices', 'Смуги', 'Paski', 'Felii', 'Franjas', 'Полосы'),
  category: 'cover',
  z: 'front',
  duration: 1100,
  params: [P_MODE, P_COUNT(5, 2, 12), P_COLOR('color', 'accent'), P_COLOR2('dark')],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const n = Math.round(Math.max(2, Math.min(12, num(ctx, 'count', 5))));
    const c1 = color(ctx, 'color', 'accent');
    const c2 = color(ctx, 'color2', 'dark');
    const h = H / n;
    return Array.from({ length: n }, (_, i) => {
      const d: Dir = i % 2 ? 'left' : 'right';
      return {
        style: {
          position: 'absolute',
          left: '0px',
          top: px(i * h - 0.5),
          width: px(W),
          height: px(h + 1),
          background: i % 2 ? c2 : c1,
        },
        anims: [
          coverAnim(
            ctx,
            i,
            n,
            i,
            { transform: off(d, -1) },
            { transform: 'translate(0%, 0%)' },
            { transform: off(d, 1) },
            0.08,
          ),
        ],
      };
    });
  },
};

const stripes: FxKind = {
  id: 'stripes',
  label: lbl(
    'Stripes',
    'Діагональні смуги',
    'Ukośne paski',
    'Dungi',
    'Rayas',
    'Диагональные полосы',
  ),
  category: 'cover',
  z: 'front',
  duration: 1300,
  params: [
    P_MODE,
    P_COUNT(7, 3, 14),
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
    P_COLOR3('dark'),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const n = Math.round(Math.max(3, Math.min(14, num(ctx, 'count', 7))));
    const colors = palette3(ctx, 'accent', 'accent2', 'dark');
    const diag = Math.hypot(W, H);
    const L = diag * 1.05;
    const h = diag / n;
    const cx = W / 2;
    const cy = H / 2;
    return Array.from({ length: n }, (_, i) => {
      const left = cx - L / 2;
      const top = cy - diag / 2 + i * h;
      const k = i % 2 ? -1 : 1;
      const t = (m: number) => `rotate(-35deg) translateX(${px(m * k * (L + 4))})`;
      return {
        style: {
          position: 'absolute',
          left: px(left),
          top: px(top - 0.5),
          width: px(L),
          height: px(h + 1),
          background: colors[i % 3],
          transformOrigin: `${px(cx - left)} ${px(cy - top)}`,
        },
        anims: [
          coverAnim(
            ctx,
            i,
            n,
            i,
            { transform: t(-1) },
            { transform: t(0) },
            { transform: t(1) },
            0.07,
          ),
        ],
      };
    });
  },
};

const doors: FxKind = {
  id: 'doors',
  label: lbl('Doors', 'Двері', 'Drzwi', 'Uși', 'Puertas', 'Двери'),
  category: 'cover',
  z: 'front',
  duration: 1200,
  params: [P_MODE, P_COLOR('color', 'accent'), P_COLOR2('light')],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const c = color(ctx, 'color', 'accent');
    const seam = color(ctx, 'color2', 'light');
    const u = unit(ctx);
    const half = (side: -1 | 1): FxNode => ({
      style: {
        position: 'absolute',
        top: '0px',
        left: side < 0 ? '0px' : px(W / 2 - 0.5),
        width: px(W / 2 + 0.5),
        height: px(H),
        background: c,
        boxShadow: side < 0 ? `inset ${px(-6 * u)} 0 0 ${seam}` : `inset ${px(6 * u)} 0 0 ${seam}`,
      },
      anims: [
        coverAnim(
          ctx,
          0,
          1,
          0,
          { transform: `translateX(${side * 101}%)` },
          { transform: 'translateX(0%)' },
          { transform: `translateX(${side * 101}%)` },
        ),
      ],
    });
    return [half(-1), half(1)];
  },
};

const circle: FxKind = {
  id: 'circle',
  label: lbl('Circle', 'Коло', 'Koło', 'Cerc', 'Círculo', 'Круг'),
  category: 'cover',
  z: 'front',
  duration: 1200,
  params: [
    P_MODE,
    P_X,
    P_Y,
    P_COUNT(2, 1, 3),
    P_COLOR('color', 'accent'),
    P_COLOR2('dark'),
    P_COLOR3('accent2'),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const x = (num(ctx, 'x', 50) / 100) * W;
    const y = (num(ctx, 'y', 50) / 100) * H;
    const R = Math.ceil(Math.hypot(Math.max(x, W - x), Math.max(y, H - y))) + 4;
    const n = Math.round(Math.max(1, Math.min(3, num(ctx, 'count', 2))));
    const colors = palette3(ctx, 'accent', 'dark', 'accent2');
    const c = (r: number) => `circle(${px(r)} at ${px(x)} ${px(y)})`;
    return Array.from({ length: n }, (_, i) => ({
      style: full(ctx, { background: colors[i % 3] }),
      anims: [
        coverAnim(
          ctx,
          i,
          n,
          n - 1 - i,
          { clipPath: c(0) },
          { clipPath: c(R) },
          { clipPath: c(0) },
          0.22,
        ),
      ],
    }));
  },
};

const flash: FxKind = {
  id: 'flash',
  label: lbl('Flash', 'Спалах', 'Błysk', 'Bliț', 'Destello', 'Вспышка'),
  category: 'accent',
  z: 'front',
  duration: 500,
  params: [P_COLOR('color', 'light'), P_OPACITY(0.95)],
  build(ctx) {
    const a = num(ctx, 'opacity', 0.95);
    return [
      {
        style: full(ctx, { background: color(ctx, 'color', 'light'), opacity: 0 }),
        anims: [
          {
            delay: 0,
            duration: ctx.duration,
            fill: 'both',
            keyframes: [
              { opacity: 0, easing: 'cubic-bezier(0.5, 0, 0.75, 0)' },
              { opacity: a, offset: 0.35, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
              { opacity: 0 },
            ],
          },
        ],
      },
    ];
  },
};

// ---------------------------------------------------------------- accents

const rings: FxKind = {
  id: 'rings',
  label: lbl('Rings', 'Кільця', 'Pierścienie', 'Inele', 'Anillos', 'Кольца'),
  category: 'accent',
  z: 'front',
  duration: 1400,
  params: [
    P_X,
    P_Y,
    P_COUNT(3, 1, 8),
    P_COLOR('color', 'accent'),
    P_COLOR2('light'),
    {
      key: 'size',
      type: 'number',
      default: 1,
      min: 0.2,
      max: 2,
      step: 0.1,
      label: lbl('Size', 'Розмір', 'Rozmiar', 'Mărime', 'Tamaño', 'Размер'),
    },
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx);
    const x = (num(ctx, 'x', 50) / 100) * W;
    const y = (num(ctx, 'y', 50) / 100) * H;
    const n = Math.round(Math.max(1, Math.min(8, num(ctx, 'count', 3))));
    const R = Math.max(W, H) * 0.62 * num(ctx, 'size', 1);
    const colors = [color(ctx, 'color', 'accent'), color(ctx, 'color2', 'light')];
    const D = ctx.duration;
    const st = (D * 0.35) / Math.max(1, n);
    return Array.from({ length: n }, (_, i) => ({
      style: {
        position: 'absolute',
        left: px(x - R),
        top: px(y - R),
        width: px(R * 2),
        height: px(R * 2),
        borderRadius: '50%',
        border: `${px(Math.max(2, (14 - i * 3) * u))} solid ${colors[i % 2]}`,
        boxSizing: 'border-box',
      },
      anims: [
        {
          delay: i * st,
          duration: D - i * st,
          fill: 'both',
          keyframes: [
            { transform: 'scale(0)', opacity: 1, easing: OUT },
            { transform: 'scale(1)', opacity: 0 },
          ],
        },
      ],
    }));
  },
};

const burst: FxKind = {
  id: 'burst',
  label: lbl('Burst', 'Вибух', 'Wybuch', 'Explozie', 'Estallido', 'Взрыв'),
  category: 'accent',
  z: 'front',
  duration: 800,
  params: [
    P_X,
    P_Y,
    P_COUNT(14, 4, 32),
    P_COLOR('color', 'accent'),
    P_COLOR2('light'),
    {
      key: 'size',
      type: 'number',
      default: 1,
      min: 0.2,
      max: 3,
      step: 0.1,
      label: lbl('Size', 'Розмір', 'Rozmiar', 'Mărime', 'Tamaño', 'Размер'),
    },
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx);
    const m = Math.min(W, H) * num(ctx, 'size', 1);
    const x = (num(ctx, 'x', 50) / 100) * W;
    const y = (num(ctx, 'y', 50) / 100) * H;
    const n = Math.round(Math.max(4, Math.min(32, num(ctx, 'count', 14))));
    const colors = [color(ctx, 'color', 'accent'), color(ctx, 'color2', 'light')];
    const th = Math.max(3, 9 * u);
    const len = m * 0.16;
    return Array.from({ length: n }, (_, i) => {
      const a = (360 / n) * i;
      const t = (r: number, s: number) => `rotate(${r2(a)}deg) translateX(${px(r)}) scaleX(${s})`;
      return {
        style: {
          position: 'absolute',
          left: px(x),
          top: px(y - th / 2),
          width: px(len),
          height: px(th),
          borderRadius: px(th),
          background: colors[i % 2],
          transformOrigin: '0px 50%',
        },
        anims: [
          {
            delay: 0,
            duration: ctx.duration,
            fill: 'both',
            keyframes: [
              { transform: t(m * 0.06, 0), easing: OUT },
              { transform: t(m * 0.16, 1), offset: 0.4, easing: 'cubic-bezier(0.5, 0, 0.75, 0)' },
              { transform: t(m * (i % 2 ? 0.34 : 0.42), 0) },
            ],
          },
        ],
      };
    });
  },
};

const confetti: FxKind = {
  id: 'confetti',
  label: lbl('Confetti', 'Конфеті', 'Konfetti', 'Confetti', 'Confeti', 'Конфетти'),
  category: 'accent',
  z: 'front',
  duration: 2400,
  params: [
    {
      key: 'mode',
      type: 'select',
      default: 'burst',
      label: lbl('Mode', 'Режим', 'Tryb', 'Mod', 'Modo', 'Режим'),
      options: [
        {
          value: 'burst',
          label: lbl('Burst', 'Вибух', 'Wybuch', 'Explozie', 'Estallido', 'Взрыв'),
        },
        { value: 'rain', label: lbl('Rain', 'Дощ', 'Deszcz', 'Ploaie', 'Lluvia', 'Дождь') },
      ],
    },
    P_X,
    { ...P_Y, default: 60 },
    P_COUNT(70, 10, 200),
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
    P_COLOR3('light'),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx);
    const rnd = rng(ctx.seed);
    const n = Math.round(Math.max(10, Math.min(200, num(ctx, 'count', 70))));
    const base = palette3(ctx, 'accent', 'accent2', 'light');
    const colors = [...base, shade(base[0], 0.35), shade(base[1], -0.2)];
    const rain = ctx.params.mode === 'rain';
    const ox = (num(ctx, 'x', 50) / 100) * W;
    const oy = (num(ctx, 'y', 60) / 100) * H;
    const D = ctx.duration;
    const nodes: FxNode[] = [];
    for (let i = 0; i < n; i++) {
      const shape = rnd();
      const w = (shape < 0.5 ? 12 : shape < 0.8 ? 16 : 8) * u * (0.8 + rnd() * 0.6);
      const h = shape < 0.5 ? w * 1.9 : shape < 0.8 ? w : w * 3.2;
      const c = colors[Math.floor(rnd() * colors.length)];
      const spin = (rnd() < 0.5 ? -1 : 1) * (360 + rnd() * 720);
      const flip = 180 + rnd() * 540;
      const style = {
        position: 'absolute',
        left: px((rain ? rnd() * W : ox) - w / 2),
        top: px((rain ? -h * 2 : oy) - h / 2),
        width: px(w),
        height: px(h),
        background: c,
        borderRadius: shape >= 0.5 && shape < 0.8 ? '50%' : px(w * 0.15),
      };
      if (rain) {
        const dur = D * (0.45 + rnd() * 0.45);
        const delay = rnd() * (D - dur);
        const sway = (rnd() - 0.5) * 120 * u;
        const fall = H + h * 4;
        nodes.push({
          style,
          anims: [
            {
              delay,
              duration: dur,
              fill: 'both',
              easing: 'linear',
              keyframes: [
                { transform: `translate(0px, 0px) rotate(0deg) rotateX(0deg)` },
                {
                  transform: `translate(${px(sway)}, ${px(fall * 0.33)}) rotate(${r2(spin / 3)}deg) rotateX(${r2(flip / 3)}deg)`,
                },
                {
                  transform: `translate(${px(-sway)}, ${px(fall * 0.66)}) rotate(${r2((spin * 2) / 3)}deg) rotateX(${r2((flip * 2) / 3)}deg)`,
                },
                {
                  transform: `translate(${px(sway * 0.5)}, ${px(fall)}) rotate(${r2(spin)}deg) rotateX(${r2(flip)}deg)`,
                },
              ],
            },
          ],
        });
      } else {
        const a = -Math.PI / 2 + (rnd() - 0.5) * Math.PI * 1.5;
        const v = Math.max(W, H) * (0.25 + rnd() * 0.45);
        const dx = Math.cos(a) * v;
        const dy = Math.sin(a) * v;
        const g = H * (0.35 + rnd() * 0.3);
        const delay = rnd() * D * 0.08;
        const dur = D * (0.75 + rnd() * 0.25) - delay;
        nodes.push({
          style,
          anims: [
            {
              delay,
              duration: dur,
              fill: 'both',
              keyframes: [
                {
                  transform: 'translate(0px, 0px) rotate(0deg) rotateX(0deg) scale(0.3)',
                  opacity: 1,
                  easing: OUT,
                },
                {
                  transform: `translate(${px(dx * 0.8)}, ${px(dy * 0.8)}) rotate(${r2(spin * 0.4)}deg) rotateX(${r2(flip * 0.4)}deg) scale(1)`,
                  opacity: 1,
                  offset: 0.3,
                  easing: 'cubic-bezier(0.4, 0, 0.9, 0.6)',
                },
                {
                  transform: `translate(${px(dx)}, ${px(dy + g)}) rotate(${r2(spin)}deg) rotateX(${r2(flip)}deg) scale(1)`,
                  opacity: 1,
                  offset: 0.85,
                },
                {
                  transform: `translate(${px(dx * 1.03)}, ${px(dy + g * 1.15)}) rotate(${r2(spin * 1.1)}deg) rotateX(${r2(flip * 1.1)}deg) scale(0.9)`,
                  opacity: 0,
                },
              ],
            },
          ],
        });
      }
    }
    return nodes;
  },
};

const sweep: FxKind = {
  id: 'sweep',
  label: lbl('Light sweep', 'Відблиск', 'Odblask', 'Reflex', 'Destello de luz', 'Блик'),
  category: 'accent',
  z: 'front',
  duration: 1100,
  params: [P_TARGET, P_COLOR('color', 'light'), P_OPACITY(0.55)],
  build(ctx) {
    const box = ctx.box ?? { x: 0, y: 0, width: ctx.canvas.width, height: ctx.canvas.height };
    const c = color(ctx, 'color', 'light');
    const a = num(ctx, 'opacity', 0.55);
    const bw = Math.max(box.width, box.height) * 0.45;
    const bh = Math.hypot(box.width, box.height) * 1.2;
    return [
      {
        style: {
          position: 'absolute',
          left: px(box.x),
          top: px(box.y),
          width: px(box.width),
          height: px(box.height),
          overflow: 'hidden',
          mixBlendMode: 'screen',
        },
        children: [
          {
            style: {
              position: 'absolute',
              left: px(-bw),
              top: px((box.height - bh) / 2),
              width: px(bw),
              height: px(bh),
              background: `linear-gradient(90deg, ${withAlpha(c, 0)}, ${withAlpha(c, a)} 50%, ${withAlpha(c, 0)})`,
            },
            anims: [
              {
                delay: 0,
                duration: ctx.duration,
                fill: 'both',
                keyframes: [
                  {
                    transform: 'translateX(0px) rotate(20deg)',
                    easing: 'cubic-bezier(0.45, 0, 0.55, 1)',
                  },
                  { transform: `translateX(${px(box.width + bw * 2)}) rotate(20deg)` },
                ],
              },
            ],
          },
        ],
      },
    ];
  },
};

const frame: FxKind = {
  id: 'frame',
  label: lbl('Drawn frame', 'Рамка', 'Ramka', 'Cadru', 'Marco', 'Рамка'),
  category: 'accent',
  z: 'front',
  duration: 1600,
  params: [
    P_COLOR('color', 'light'),
    {
      key: 'inset',
      type: 'number',
      default: 4,
      min: 0,
      max: 20,
      label: lbl('Inset, %', 'Відступ, %', 'Odstęp, %', 'Margine, %', 'Margen, %', 'Отступ, %'),
    },
    {
      key: 'width',
      type: 'number',
      default: 4,
      min: 1,
      max: 30,
      label: lbl('Line width', 'Товщина', 'Grubość', 'Grosime', 'Grosor', 'Толщина'),
    },
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx);
    const m = (num(ctx, 'inset', 4) / 100) * Math.min(W, H);
    const d = `M ${r2(m)} ${r2(m)} H ${r2(W - m)} V ${r2(H - m)} H ${r2(m)} Z`;
    return [
      {
        style: full(ctx),
        path: {
          d,
          stroke: color(ctx, 'color', 'light'),
          width: num(ctx, 'width', 4) * u,
          cap: 'square',
        },
        anims: [
          {
            target: 'path',
            delay: 0,
            duration: Math.min(ctx.duration, 1400),
            fill: 'both',
            keyframes: [
              { strokeDashoffset: 1, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' },
              { strokeDashoffset: 0 },
            ],
          },
        ],
      },
    ];
  },
};

const underline: FxKind = {
  id: 'underline',
  label: lbl(
    'Marker underline',
    'Підкреслення',
    'Podkreślenie',
    'Subliniere',
    'Subrayado',
    'Подчёркивание',
  ),
  category: 'accent',
  z: 'front',
  duration: 900,
  params: [
    P_TARGET,
    {
      key: 'shape',
      type: 'select',
      default: 'underline',
      label: lbl('Shape', 'Форма', 'Kształt', 'Formă', 'Forma', 'Форма'),
      options: [
        {
          value: 'underline',
          label: lbl('Underline', 'Лінія', 'Linia', 'Linie', 'Línea', 'Линия'),
        },
        {
          value: 'circle',
          label: lbl('Circle', 'Обвести', 'Obwódka', 'Încercuire', 'Círculo', 'Обвести'),
        },
        {
          value: 'strike',
          label: lbl('Strike', 'Закреслити', 'Przekreślenie', 'Tăiere', 'Tachado', 'Зачеркнуть'),
        },
      ],
    },
    P_COLOR('color', 'accent'),
  ],
  build(ctx) {
    const b = ctx.box ?? {
      x: ctx.canvas.width * 0.2,
      y: ctx.canvas.height * 0.45,
      width: ctx.canvas.width * 0.6,
      height: ctx.canvas.height * 0.1,
    };
    const u = unit(ctx);
    const shape = str(ctx, 'shape', 'underline');
    const c = color(ctx, 'color', 'accent');
    let d: string;
    let w: number;
    if (shape === 'circle') {
      const cx = b.x + b.width / 2;
      const cy = b.y + b.height / 2;
      const rx = b.width * 0.58;
      const ry = b.height * 0.75;
      // A hand-drawn loop: slightly more than a full turn, tilted
      d = `M ${r2(cx - rx * 0.9)} ${r2(cy - ry * 0.55)} C ${r2(cx - rx * 0.2)} ${r2(cy - ry * 1.25)}, ${r2(cx + rx * 1.15)} ${r2(cy - ry * 0.95)}, ${r2(cx + rx)} ${r2(cy + ry * 0.1)} S ${r2(cx - rx * 0.1)} ${r2(cy + ry * 1.25)}, ${r2(cx - rx * 1.02)} ${r2(cy + ry * 0.25)} S ${r2(cx - rx * 0.1)} ${r2(cy - ry * 1.05)}, ${r2(cx + rx * 0.45)} ${r2(cy - ry * 0.9)}`;
      w = Math.max(4 * u, b.height * 0.06);
    } else if (shape === 'strike') {
      const y = b.y + b.height * 0.55;
      d = `M ${r2(b.x - b.width * 0.03)} ${r2(y + b.height * 0.05)} Q ${r2(b.x + b.width / 2)} ${r2(y - b.height * 0.1)} ${r2(b.x + b.width * 1.03)} ${r2(y)}`;
      w = Math.max(5 * u, b.height * 0.1);
    } else {
      const y = b.y + b.height + Math.max(6 * u, b.height * 0.06);
      d = `M ${r2(b.x)} ${r2(y)} Q ${r2(b.x + b.width * 0.5)} ${r2(y + b.height * 0.12)} ${r2(b.x + b.width)} ${r2(y - b.height * 0.04)}`;
      w = Math.max(6 * u, b.height * 0.09);
    }
    return [
      {
        style: full(ctx),
        path: { d, stroke: c, width: w, cap: 'round' },
        anims: [
          {
            target: 'path',
            delay: 0,
            duration: ctx.duration,
            fill: 'both',
            keyframes: [
              { strokeDashoffset: 1, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' },
              { strokeDashoffset: 0 },
            ],
          },
        ],
      },
    ];
  },
};

const lines: FxKind = {
  id: 'lines',
  label: lbl(
    'Speed lines',
    'Лінії швидкості',
    'Linie prędkości',
    'Linii de viteză',
    'Líneas de velocidad',
    'Линии скорости',
  ),
  category: 'accent',
  z: 'front',
  duration: 1200,
  params: [
    { ...P_DIR, default: 'left' },
    P_COUNT(16, 4, 60),
    P_COLOR('color', 'light'),
    P_COLOR2('accent'),
    P_OPACITY(0.8),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx);
    const rnd = rng(ctx.seed);
    const d = dirOf(ctx, 'left');
    const n = Math.round(Math.max(4, Math.min(60, num(ctx, 'count', 16))));
    const colors = [color(ctx, 'color', 'light'), color(ctx, 'color2', 'accent')];
    const a = num(ctx, 'opacity', 0.8);
    const horizontal = d === 'left' || d === 'right';
    const span = horizontal ? W : H;
    const D = ctx.duration;
    return Array.from({ length: n }, (_, i) => {
      const len = span * (0.15 + rnd() * 0.35);
      const th = (2 + rnd() * 5) * u;
      const pos = rnd() * (horizontal ? H : W);
      const dur = D * (0.25 + rnd() * 0.25);
      const delay = rnd() * (D - dur);
      const sign = d === 'right' || d === 'down' ? 1 : -1;
      const from = sign > 0 ? -len : span;
      const to = sign > 0 ? span : -len;
      const t = (v: number) => (horizontal ? `translateX(${px(v)})` : `translateY(${px(v)})`);
      return {
        style: horizontal
          ? {
              position: 'absolute',
              left: '0px',
              top: px(pos),
              width: px(len),
              height: px(th),
              borderRadius: px(th),
              background: colors[i % 4 === 3 ? 1 : 0],
              opacity: a,
            }
          : {
              position: 'absolute',
              top: '0px',
              left: px(pos),
              height: px(len),
              width: px(th),
              borderRadius: px(th),
              background: colors[i % 4 === 3 ? 1 : 0],
              opacity: a,
            },
        anims: [
          {
            delay,
            duration: dur,
            fill: 'both',
            keyframes: [{ transform: t(from) }, { transform: t(to) }],
            easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
          },
        ],
      };
    });
  },
};

const glitch: FxKind = {
  id: 'glitch',
  label: lbl('Glitch', 'Глітч', 'Glitch', 'Glitch', 'Glitch', 'Глитч'),
  category: 'accent',
  z: 'front',
  duration: 700,
  params: [P_COUNT(9, 3, 24), P_COLOR('color', 'accent'), P_COLOR2('accent2'), P_COLOR3('light')],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const rnd = rng(ctx.seed);
    const n = Math.round(Math.max(3, Math.min(24, num(ctx, 'count', 9))));
    const colors = palette3(ctx, 'accent', 'accent2', 'light');
    const steps = 8;
    return Array.from({ length: n }, (_, i) => {
      const h = H * (0.015 + rnd() * 0.08);
      const keyframes: CssKeyframe[] = [];
      for (let s = 0; s <= steps; s++) {
        const on = s < steps && rnd() < 0.55;
        keyframes.push({
          transform: `translate(${px((rnd() - 0.5) * W * 0.5)}, ${px(rnd() * (H - h))})`,
          opacity: on ? 0.9 : 0,
          offset: s / steps,
          easing: 'steps(1, end)',
        });
      }
      return {
        style: {
          position: 'absolute',
          left: px(-W * 0.1),
          top: '0px',
          width: px(W * (0.4 + rnd() * 0.8)),
          height: px(h),
          background: colors[i % 3],
          mixBlendMode: 'difference',
          opacity: 0,
        },
        anims: [{ delay: 0, duration: ctx.duration, fill: 'both', easing: 'linear', keyframes }],
      };
    });
  },
};

const letterbox: FxKind = {
  id: 'letterbox',
  label: lbl(
    'Letterbox',
    'Кінорамка',
    'Kadr kinowy',
    'Bandă cinema',
    'Bandas de cine',
    'Кинорамка',
  ),
  category: 'accent',
  z: 'front',
  duration: 5000,
  params: [
    {
      key: 'mode',
      type: 'select',
      default: 'in-out',
      label: lbl('Mode', 'Режим', 'Tryb', 'Mod', 'Modo', 'Режим'),
      options: [
        {
          value: 'in-out',
          label: lbl(
            'In and out',
            'Поява й зникнення',
            'Wejście i wyjście',
            'Intrare și ieșire',
            'Entrar y salir',
            'Появиться и уйти',
          ),
        },
        {
          value: 'hold',
          label: lbl('Stay', 'Залишатися', 'Zostań', 'Rămâne', 'Permanecer', 'Оставаться'),
        },
      ],
    },
    P_COLOR('color', 'dark'),
    {
      key: 'size',
      type: 'number',
      default: 11,
      min: 3,
      max: 25,
      label: lbl('Height, %', 'Висота, %', 'Wysokość, %', 'Înălțime, %', 'Altura, %', 'Высота, %'),
    },
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const h = (num(ctx, 'size', 11) / 100) * H;
    const c = color(ctx, 'color', 'dark');
    const D = ctx.duration;
    const inD = Math.min(700, D * 0.3);
    const outD = ctx.params.mode === 'hold' ? 0 : Math.min(700, D * 0.3);
    const bar = (top: boolean): FxNode => ({
      style: {
        position: 'absolute',
        left: '0px',
        top: top ? '0px' : px(H - h),
        width: px(W),
        height: px(h + 1),
        background: c,
      },
      anims: [
        {
          delay: 0,
          duration: D,
          fill: 'both',
          keyframes: [
            { transform: `translateY(${top ? -101 : 101}%)`, offset: 0, easing: OUT },
            { transform: 'translateY(0%)', offset: inD / D },
            ...(outD
              ? [
                  { transform: 'translateY(0%)', offset: 1 - outD / D, easing: IO },
                  { transform: `translateY(${top ? -101 : 101}%)`, offset: 1 },
                ]
              : [{ transform: 'translateY(0%)', offset: 1 }]),
          ],
        },
      ],
    });
    return [bar(true), bar(false)];
  },
};

// ---------------------------------------------------------------- ambient (backdrops)

const aurora: FxKind = {
  id: 'aurora',
  label: lbl('Aurora gradient', 'Аврора', 'Zorza', 'Auroră', 'Aurora', 'Аврора'),
  category: 'ambient',
  z: 'back',
  duration: 5000,
  params: [P_COLOR('color', 'accent'), P_COLOR2('accent2'), P_COLOR3('light'), P_OPACITY(0.75)],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const rnd = rng(ctx.seed);
    const colors = palette3(ctx, 'accent', 'accent2', 'light');
    const a = num(ctx, 'opacity', 0.75);
    const size = Math.max(W, H) * 0.9;
    return [0, 1, 2, 3].map((i) => {
      const pts = Array.from({ length: 4 }, () => [
        (rnd() - 0.5) * W * 0.6,
        (rnd() - 0.5) * H * 0.6,
      ]);
      const cx = W * (0.2 + rnd() * 0.6);
      const cy = H * (0.2 + rnd() * 0.6);
      const c = colors[i % 3];
      return {
        style: {
          position: 'absolute',
          left: px(cx - size / 2),
          top: px(cy - size / 2),
          width: px(size),
          height: px(size),
          borderRadius: '50%',
          background: `radial-gradient(circle, ${withAlpha(c, a)} 0%, ${withAlpha(c, a * 0.45)} 30%, ${withAlpha(c, 0)} 68%)`,
        },
        anims: [
          {
            delay: 0,
            duration: ctx.duration,
            fill: 'both',
            easing: 'linear',
            keyframes: [
              {
                transform: `translate(${px(pts[0][0])}, ${px(pts[0][1])}) scale(0.9)`,
                opacity: 0,
                easing: 'ease-in-out',
              },
              {
                transform: `translate(${px(pts[1][0])}, ${px(pts[1][1])}) scale(1.1)`,
                opacity: 1,
                offset: 0.3,
                easing: 'ease-in-out',
              },
              {
                transform: `translate(${px(pts[2][0])}, ${px(pts[2][1])}) scale(0.95)`,
                opacity: 1,
                offset: 0.7,
                easing: 'ease-in-out',
              },
              {
                transform: `translate(${px(pts[3][0])}, ${px(pts[3][1])}) scale(1.05)`,
                opacity: 1,
              },
            ],
          },
        ],
      };
    });
  },
};

const grid: FxKind = {
  id: 'grid',
  label: lbl(
    'Dot grid',
    'Сітка точок',
    'Siatka kropek',
    'Grilă de puncte',
    'Cuadrícula de puntos',
    'Сетка точек',
  ),
  category: 'ambient',
  z: 'back',
  duration: 5000,
  params: [
    P_COLOR('color', 'ink'),
    P_OPACITY(0.3),
    {
      key: 'cell',
      type: 'number',
      default: 48,
      min: 16,
      max: 200,
      label: lbl(
        'Cell, px',
        'Клітинка, px',
        'Komórka, px',
        'Celulă, px',
        'Celda, px',
        'Ячейка, px',
      ),
    },
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx);
    const cell = num(ctx, 'cell', 48) * u;
    const c = color(ctx, 'color', 'ink');
    const a = num(ctx, 'opacity', 0.3);
    const dot = Math.max(1.5, 3 * u);
    const moves = Math.max(1, Math.round(ctx.duration / 2500));
    return [
      {
        style: {
          position: 'absolute',
          left: px(-cell * 2),
          top: px(-cell * 2),
          width: px(W + cell * 4),
          height: px(H + cell * 4),
          backgroundImage: `radial-gradient(circle, ${c} ${px(dot)}, transparent ${px(dot + 0.8)})`,
          backgroundSize: `${px(cell)} ${px(cell)}`,
          opacity: 0,
        },
        anims: [
          {
            delay: 0,
            duration: ctx.duration,
            fill: 'both',
            keyframes: [
              { transform: 'translate(0px, 0px)', opacity: 0 },
              { opacity: a, offset: 0.15 },
              { transform: `translate(${px(cell * moves)}, ${px(cell * moves)})`, opacity: a },
            ],
          },
        ],
      },
    ];
  },
};

const shapes: FxKind = {
  id: 'shapes',
  label: lbl('Floating shapes', 'Фігури', 'Kształty', 'Forme', 'Formas', 'Фигуры'),
  category: 'ambient',
  z: 'back',
  duration: 5000,
  params: [
    P_COUNT(10, 3, 30),
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
    P_COLOR3('light'),
    {
      key: 'size',
      type: 'number',
      default: 1,
      min: 0.3,
      max: 3,
      step: 0.1,
      label: lbl('Size', 'Розмір', 'Rozmiar', 'Mărime', 'Tamaño', 'Размер'),
    },
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx) * num(ctx, 'size', 1);
    const rnd = rng(ctx.seed);
    const n = Math.round(Math.max(3, Math.min(30, num(ctx, 'count', 10))));
    const colors = palette3(ctx, 'accent', 'accent2', 'light');
    const D = ctx.duration;
    return Array.from({ length: n }, (_, i) => {
      const kind = i % 5;
      const s = (36 + rnd() * 70) * u;
      const c = colors[Math.floor(rnd() * 3)];
      const x = rnd() * W;
      const y = rnd() * H;
      const base = {
        position: 'absolute',
        left: px(x - s / 2),
        top: px(y - s / 2),
        width: px(s),
        height: px(s),
      };
      const style: Record<string, string | number> =
        kind === 0
          ? {
              ...base,
              borderRadius: '50%',
              border: `${px(Math.max(2, s * 0.14))} solid ${c}`,
              boxSizing: 'border-box',
            }
          : kind === 1
            ? {
                ...base,
                width: px(s * 0.45),
                height: px(s * 0.45),
                borderRadius: '50%',
                background: c,
              }
            : kind === 2
              ? { ...base, background: c, clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }
              : kind === 3
                ? {
                    ...base,
                    background: c,
                    clipPath:
                      'polygon(38% 0%, 62% 0%, 62% 38%, 100% 38%, 100% 62%, 62% 62%, 62% 100%, 38% 100%, 38% 62%, 0% 62%, 0% 38%, 38% 38%)',
                  }
                : {
                    ...base,
                    border: `${px(Math.max(2, s * 0.12))} solid ${c}`,
                    boxSizing: 'border-box',
                    borderRadius: px(s * 0.18),
                  };
      const pop = 0.06 + (i / n) * 0.3;
      const r0 = (rnd() - 0.5) * 60;
      const r1 = r0 + (rnd() - 0.5) * 120;
      const dx = (rnd() - 0.5) * 120 * u;
      const dy = -(30 + rnd() * 90) * u;
      return {
        style,
        anims: [
          {
            delay: 0,
            duration: D,
            fill: 'both',
            keyframes: [
              {
                transform: `translate(0px, 0px) rotate(${r2(r0 - 90)}deg) scale(0)`,
                offset: 0,
                easing: 'steps(1, end)',
              },
              {
                transform: `translate(0px, 0px) rotate(${r2(r0 - 90)}deg) scale(0)`,
                offset: pop,
                easing: BACK,
              },
              {
                transform: `translate(0px, 0px) rotate(${r2(r0)}deg) scale(1)`,
                offset: Math.min(0.95, pop + 0.12),
                easing: 'ease-in-out',
              },
              { transform: `translate(${px(dx)}, ${px(dy)}) rotate(${r2(r1)}deg) scale(1)` },
            ],
          },
        ],
      };
    });
  },
};

const marquee: FxKind = {
  id: 'marquee',
  label: lbl(
    'Giant marquee',
    'Бігучий рядок',
    'Pasek przewijany',
    'Text derulant',
    'Marquesina',
    'Бегущая строка',
  ),
  category: 'ambient',
  z: 'back',
  duration: 5000,
  params: [
    P_TEXT,
    P_COUNT(3, 1, 6),
    P_COLOR('color', 'ink'),
    P_OPACITY(0.16),
    {
      key: 'outline',
      type: 'select',
      default: 'outline',
      label: lbl('Style', 'Стиль', 'Styl', 'Stil', 'Estilo', 'Стиль'),
      options: [
        {
          value: 'outline',
          label: lbl('Outline', 'Контур', 'Kontur', 'Contur', 'Contorno', 'Контур'),
        },
        {
          value: 'solid',
          label: lbl('Solid', 'Заливка', 'Wypełnienie', 'Plin', 'Sólido', 'Заливка'),
        },
      ],
    },
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const u = unit(ctx);
    const rows = Math.round(Math.max(1, Math.min(6, num(ctx, 'count', 3))));
    const text = str(ctx, 'text', ctx.text || 'SALE')
      .replace(/\s+/g, ' ')
      .trim();
    const c = color(ctx, 'color', 'ink');
    const a = num(ctx, 'opacity', 0.16);
    const outline = ctx.params.outline !== 'solid';
    const fs = (H / rows) * 0.92;
    const upper = ctx.font.transform === 'uppercase';
    const unitW = ([...text].length + 3) * fs * (upper ? 0.68 : 0.58);
    const travel = Math.max(W * 0.35, (ctx.duration / 1000) * W * 0.12);
    const reps = Math.max(2, Math.ceil((W + travel) / Math.max(1, unitW)) + 1);
    const content = Array.from({ length: reps }, () => text).join(' • ');
    return Array.from({ length: rows }, (_, i) => {
      const k = i % 2 ? 1 : -1;
      return {
        text: content,
        style: {
          position: 'absolute',
          left: px(k < 0 ? 0 : -travel),
          top: px(i * (H / rows) + (H / rows - fs * 1.05) / 2),
          whiteSpace: 'nowrap',
          fontFamily: ctx.font.family,
          fontWeight: ctx.font.weight,
          fontStyle: ctx.font.style,
          textTransform: ctx.font.transform,
          fontSize: px(fs),
          lineHeight: '1.05',
          color: outline ? 'transparent' : c,
          WebkitTextStroke: outline ? `${px(Math.max(1.5, 3 * u))} ${c}` : '',
          opacity: a,
        },
        anims: [
          {
            delay: 0,
            duration: ctx.duration,
            fill: 'both',
            easing: 'linear',
            keyframes: [
              { transform: 'translateX(0px)' },
              { transform: `translateX(${px(k * travel)})` },
            ],
          },
        ],
      };
    });
  },
};

// ---------------------------------------------------------------- text: title card

const titleCard: FxKind = {
  id: 'title',
  label: lbl('Title card', 'Титр', 'Plansza tytułowa', 'Titlu', 'Cartela de título', 'Титр'),
  category: 'text',
  z: 'front',
  duration: 1600,
  params: [
    P_TEXT,
    {
      key: 'style',
      type: 'select',
      default: 'slam',
      label: lbl('Animation', 'Анімація', 'Animacja', 'Animație', 'Animación', 'Анимация'),
      options: [
        { value: 'slam', label: lbl('Slam', 'Удар', 'Uderzenie', 'Lovitură', 'Golpe', 'Удар') },
        {
          value: 'rise',
          label: lbl(
            'Mask rise',
            'Підйом з маски',
            'Wysunięcie',
            'Ridicare din mască',
            'Subida con máscara',
            'Подъём из маски',
          ),
        },
        {
          value: 'type',
          label: lbl(
            'Typewriter',
            'Друкарка',
            'Maszyna do pisania',
            'Mașină de scris',
            'Máquina de escribir',
            'Печатная машинка',
          ),
        },
        {
          value: 'blur',
          label: lbl('Focus', 'Фокус', 'Ostrość', 'Focalizare', 'Enfoque', 'Фокус'),
        },
        {
          value: 'track',
          label: lbl('Tracking', 'Розрядка', 'Rozstrzelenie', 'Spațiere', 'Espaciado', 'Разрядка'),
        },
      ],
    },
    {
      key: 'exit',
      type: 'select',
      default: 'cut',
      label: lbl('Exit', 'Зникнення', 'Wyjście', 'Ieșire', 'Salida', 'Исчезновение'),
      options: [
        {
          value: 'cut',
          label: lbl(
            'Cut (under a transition)',
            'Склейка (під переходом)',
            'Cięcie (pod przejściem)',
            'Tăietură (sub tranziție)',
            'Corte (bajo una transición)',
            'Склейка (под переходом)',
          ),
        },
        {
          value: 'wipe',
          label: lbl(
            'Wipe up',
            'Шторка вгору',
            'Kurtyna w górę',
            'Cortină în sus',
            'Cortinilla arriba',
            'Шторка вверх',
          ),
        },
        {
          value: 'iris',
          label: lbl('Iris', 'Діафрагма', 'Przesłona', 'Diafragmă', 'Iris', 'Диафрагма'),
        },
        {
          value: 'fade',
          label: lbl('Fade', 'Згасання', 'Wygaszenie', 'Estompare', 'Fundido', 'Затухание'),
        },
      ],
    },
    P_COLOR('bg', 'dark', lbl('Background', 'Фон', 'Tło', 'Fundal', 'Fondo', 'Фон')),
    P_COLOR('color', 'light', lbl('Text', 'Текст', 'Tekst', 'Text', 'Texto', 'Текст')),
    P_COLOR(
      'accentColor',
      'accent',
      lbl(
        'Accent word',
        'Акцентне слово',
        'Słowo akcentu',
        'Cuvânt accent',
        'Palabra destacada',
        'Акцентное слово',
      ),
    ),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const words = headlineWords(ctx);
    const upper = ctx.font.transform === 'uppercase';
    const pad = Math.min(W, H) * 0.08;
    const f = fitFont(words, W - pad * 2, H - pad * 2, upper, Math.min(W, H) * 0.2);
    const style = str(ctx, 'style', 'slam');
    const exit = str(ctx, 'exit', 'cut');
    const bg = color(ctx, 'bg', 'dark');
    const fg = readable(bg, color(ctx, 'color', 'light'), ctx.palette.light, ctx.palette.dark);
    const accentColor = readable(
      bg,
      color(ctx, 'accentColor', 'accent'),
      ctx.palette.accent2,
      shade(ctx.palette.accent, 0.55),
      fg,
    );
    const u = unit(ctx);
    const exitD = exit === 'cut' ? 0 : Math.min(500, D * 0.25);
    const showUntil = D - exitD;
    const enterSpan = Math.max(200, Math.min(showUntil * 0.6, words.length * 220));
    const chars = words.reduce((s, w) => s + [...w].length, 0);
    const wordStagger =
      words.length > 1 ? (enterSpan - Math.min(500, enterSpan * 0.6)) / (words.length - 1) : 0;
    const wordDur = Math.min(500, enterSpan * 0.6);

    let charIndex = 0;
    const wordNodes: FxNode[] = words.map((w, i) => {
      const isAccent = words.length > 1 && i === words.length - 1;
      const c = isAccent ? accentColor : fg;
      const start = i * wordStagger;
      const base: Record<string, string | number> = {
        display: 'inline-block',
        margin: `0 ${px(f * 0.13)}`,
        color: c,
        whiteSpace: 'pre',
      };
      if (style === 'type') {
        const perChar = Math.max(16, (enterSpan * 0.9) / Math.max(1, chars));
        return {
          style: base,
          children: [...w].map((ch) => {
            const at = charIndex++ * perChar;
            return {
              text: ch,
              style: { display: 'inline-block', whiteSpace: 'pre' },
              anims: [
                {
                  delay: at,
                  duration: 1,
                  fill: 'both',
                  keyframes: [{ opacity: 0 }, { opacity: 1 }],
                },
              ],
            };
          }),
        };
      }
      if (style === 'rise') {
        return {
          style: { ...base, overflow: 'hidden', verticalAlign: 'top', paddingBottom: px(f * 0.08) },
          children: [
            {
              text: w,
              style: { display: 'inline-block' },
              anims: [
                {
                  delay: start,
                  duration: wordDur * 1.3,
                  fill: 'both',
                  keyframes: [
                    { transform: 'translateY(110%)', easing: OUT },
                    { transform: 'translateY(0%)' },
                  ],
                },
              ],
            },
          ],
        };
      }
      const keyframes: CssKeyframe[] =
        style === 'blur'
          ? [
              { opacity: 0, filter: `blur(${px(24 * u)})`, transform: 'scale(1.15)', easing: OUT },
              { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' },
            ]
          : style === 'track'
            ? [
                { opacity: 0, letterSpacing: '0.6em', easing: OUT },
                { opacity: 1, letterSpacing: '0em' },
              ]
            : [
                {
                  opacity: 0,
                  transform: 'scale(2.4)',
                  filter: `blur(${px(10 * u)})`,
                  easing: 'cubic-bezier(0.2, 0.9, 0.25, 1)',
                },
                {
                  opacity: 1,
                  transform: 'scale(0.94)',
                  filter: 'blur(0px)',
                  offset: 0.55,
                  easing: 'ease-out',
                },
                { opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' },
              ];
      return {
        text: w,
        style: base,
        anims: [{ delay: start, duration: wordDur, fill: 'both', keyframes }],
      };
    });

    // Fixed lines (no reflow while words move or track out); the player shrinks the font to fit
    const rows: FxNode[] = [];
    let k = 0;
    for (const line of balancedLines(words, f, W - pad * 2, upper)) {
      rows.push({
        style: {
          display: 'flex',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
        },
        children: wordNodes.slice(k, k + line.length),
      });
      k += line.length;
    }
    const card: FxNode = {
      style: full(ctx, {
        background: bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        boxSizing: 'border-box',
        padding: px(pad),
        overflow: 'hidden',
        fontFamily: ctx.font.family,
        fontWeight: ctx.font.weight,
        fontStyle: ctx.font.style,
        textTransform: ctx.font.transform,
        fontSize: px(f),
        lineHeight: '1.08',
        letterSpacing: '-0.01em',
      }),
      fit: true,
      children: rows,
      anims: [],
    };
    if (style === 'slam' || style === 'blur') {
      // A slow push while the words land keeps the card alive
      card.anims!.push({
        delay: 0,
        duration: showUntil,
        fill: 'both',
        keyframes: [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }],
        easing: 'linear',
      });
    }
    if (exit === 'wipe') {
      card.anims!.push({
        delay: showUntil,
        duration: exitD,
        fill: 'forwards',
        keyframes: [
          { clipPath: 'inset(0% 0% 0% 0%)', easing: IN },
          { clipPath: 'inset(0% 0% 100% 0%)' },
        ],
      });
    } else if (exit === 'iris') {
      const R = Math.ceil(Math.hypot(W, H) / 2) + 2;
      card.anims!.push({
        delay: showUntil,
        duration: exitD,
        fill: 'forwards',
        keyframes: [
          { clipPath: `circle(${px(R)} at 50% 50%)`, easing: IN },
          { clipPath: 'circle(0px at 50% 50%)' },
        ],
      });
    } else if (exit === 'fade') {
      card.anims!.push({
        delay: showUntil,
        duration: exitD,
        fill: 'forwards',
        keyframes: [{ opacity: 1 }, { opacity: 0 }],
      });
    }
    return [card];
  },
};

// ---------------------------------------------------------------- captions

type CaptionStyle = 'blink' | 'trailer' | 'slide' | 'pop' | 'karaoke';

/** Words with typographic glue (a dash stays with the word before, a short word with the next). */
function glueWords(text: string): string[] {
  const words: string[] = [];
  let carry = '';
  for (const w of text.split(/\s+/).filter(Boolean)) {
    if (/^[—–-]+$/.test(w) && words.length) words[words.length - 1] += ' ' + w;
    else if ([...w].length <= 2 && /\p{L}/u.test(w)) carry += w + ' ';
    else {
      words.push(carry + w);
      carry = '';
    }
  }
  if (carry) {
    if (words.length) words[words.length - 1] += ' ' + carry.trim();
    else words.push(carry.trim());
  }
  return words;
}

/**
 * Script split into the chunks shown one after another. Lines, "/" and "|" always split; then
 * one word per chunk, or short phrases that end at punctuation.
 */
export function captionChunks(text: string, mode: 'word' | 'phrase' | 'line'): string[][] {
  const out: string[][] = [];
  for (const part of text.split(/\n|\s\/\s|\|/)) {
    const words = glueWords(part.trim());
    if (!words.length) continue;
    if (mode === 'line') {
      out.push(words);
      continue;
    }
    if (mode === 'word') {
      for (const w of words) out.push([w]);
      continue;
    }
    let cur: string[] = [];
    let chars = 0;
    for (const w of words) {
      const n = [...w].length;
      if (cur.length && (cur.length >= 3 || chars + n > 22)) {
        // A phrase never ends on a short function word ("без", "и", "for"): it moves on
        const tail =
          cur.length > 1 && /^\p{Ll}{1,3}$/u.test(cur[cur.length - 1]) ? cur.pop()! : null;
        out.push(cur);
        cur = tail ? [tail] : [];
        chars = tail ? [...tail].length + 1 : 0;
      }
      cur.push(w);
      chars += n + 1;
      if (/[.,!?;:…]$/.test(w)) {
        out.push(cur);
        cur = [];
        chars = 0;
      }
    }
    if (cur.length) out.push(cur);
  }
  return out.slice(0, 60);
}

/** Start and length of every chunk: longer chunks stay longer, as when read aloud. */
function chunkTimes(chunks: string[][], total: number): Array<{ at: number; len: number }> {
  const weights = chunks.map(
    (c) => c.reduce((s, w) => s + [...w].length, 0) + 6 + (/[.!?…]$/.test(c[c.length - 1]) ? 4 : 0),
  );
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  let at = 0;
  return weights.map((w) => {
    const len = (total * w) / sum;
    const out = { at, len };
    at += len;
    return out;
  });
}

const visible = (at: number, len: number): FxAnim => ({
  delay: at,
  duration: Math.max(1, len),
  fill: 'none',
  keyframes: [{ visibility: 'visible' }, { visibility: 'visible' }],
});

const captions: FxKind = {
  id: 'captions',
  label: lbl(
    'Kinetic captions',
    'Кінетичні титри',
    'Napisy kinetyczne',
    'Subtitrări cinetice',
    'Rótulos cinéticos',
    'Кинетические титры',
  ),
  category: 'text',
  z: 'front',
  duration: 3000,
  params: [
    {
      key: 'text',
      type: 'longtext',
      default: '',
      label: lbl(
        'Text (empty = the design’s text). New line or / = new card',
        'Текст (порожньо = текст дизайну). Новий рядок або / = нова картка',
        'Tekst (pusty = tekst projektu). Nowa linia lub / = nowa karta',
        'Text (gol = textul designului). Rând nou sau / = cartelă nouă',
        'Texto (vacío = texto del diseño). Nueva línea o / = nueva tarjeta',
        'Текст (пусто = текст дизайна). Новая строка или / = новая карточка',
      ),
    },
    {
      key: 'style',
      type: 'select',
      default: 'blink',
      label: lbl('Style', 'Стиль', 'Styl', 'Stil', 'Estilo', 'Стиль'),
      options: [
        {
          value: 'blink',
          label: lbl(
            'Hard cuts, full screen',
            'Різкі склейки на весь екран',
            'Ostre cięcia, cały ekran',
            'Tăieturi rapide, tot ecranul',
            'Cortes secos a pantalla completa',
            'Резкие склейки на весь экран',
          ),
        },
        {
          value: 'trailer',
          label: lbl('Trailer', 'Трейлер', 'Zwiastun', 'Trailer', 'Tráiler', 'Трейлер'),
        },
        {
          value: 'slide',
          label: lbl(
            'Rise from mask',
            'Виїзд з маски',
            'Wysuwanie z maski',
            'Ridicare din mască',
            'Subida con máscara',
            'Выезд из маски',
          ),
        },
        {
          value: 'pop',
          label: lbl(
            'Word pop (over the video)',
            'Слова вистрибують (поверх відео)',
            'Wyskakujące słowa (na wideo)',
            'Cuvinte care apar (peste video)',
            'Palabras que saltan (sobre el vídeo)',
            'Слова выпрыгивают (поверх видео)',
          ),
        },
        {
          value: 'karaoke',
          label: lbl(
            'Karaoke subtitles',
            'Субтитри-караоке',
            'Napisy karaoke',
            'Subtitrare karaoke',
            'Subtítulos karaoke',
            'Субтитры-караоке',
          ),
        },
      ],
    },
    {
      key: 'source',
      type: 'select',
      default: 'all',
      label: lbl(
        'Empty text means',
        'Порожній текст',
        'Pusty tekst to',
        'Text gol înseamnă',
        'Texto vacío significa',
        'Пустой текст',
      ),
      options: [
        {
          value: 'all',
          label: lbl(
            'All text of the design',
            'Весь текст дизайну',
            'Cały tekst projektu',
            'Tot textul designului',
            'Todo el texto del diseño',
            'Весь текст дизайна',
          ),
        },
        {
          value: 'headline',
          label: lbl('Headline', 'Заголовок', 'Nagłówek', 'Titlu', 'Título', 'Заголовок'),
        },
      ],
    },
    {
      key: 'chunk',
      type: 'select',
      default: 'auto',
      label: lbl('Per card', 'На картці', 'Na karcie', 'Pe cartelă', 'Por tarjeta', 'На карточке'),
      options: [
        { value: 'auto', label: lbl('Auto', 'Авто', 'Auto', 'Auto', 'Auto', 'Авто') },
        {
          value: 'word',
          label: lbl('A word', 'Слово', 'Słowo', 'Un cuvânt', 'Una palabra', 'Слово'),
        },
        {
          value: 'phrase',
          label: lbl('A phrase', 'Фраза', 'Fraza', 'O frază', 'Una frase', 'Фраза'),
        },
        {
          value: 'line',
          label: lbl(
            'A line of the text',
            'Рядок тексту',
            'Linia tekstu',
            'Un rând',
            'Una línea',
            'Строка текста',
          ),
        },
      ],
    },
    {
      key: 'backdrop',
      type: 'select',
      default: 'auto',
      label: lbl('Backdrop', 'Підкладка', 'Podkład', 'Fundal', 'Fondo', 'Подложка'),
      options: [
        { value: 'auto', label: lbl('Auto', 'Авто', 'Auto', 'Auto', 'Auto', 'Авто') },
        {
          value: 'solid',
          label: lbl('Solid', 'Суцільна', 'Pełny', 'Plin', 'Sólido', 'Сплошная'),
        },
        {
          value: 'dim',
          label: lbl(
            'Dim',
            'Затемнення',
            'Przyciemnienie',
            'Întunecare',
            'Oscurecer',
            'Затемнение',
          ),
        },
        { value: 'none', label: lbl('None', 'Немає', 'Brak', 'Fără', 'Ninguno', 'Нет') },
      ],
    },
    {
      key: 'position',
      type: 'select',
      default: 'auto',
      label: lbl('Position', 'Положення', 'Pozycja', 'Poziție', 'Posición', 'Положение'),
      options: [
        { value: 'auto', label: lbl('Auto', 'Авто', 'Auto', 'Auto', 'Auto', 'Авто') },
        {
          value: 'center',
          label: lbl('Center', 'Центр', 'Środek', 'Centru', 'Centro', 'Центр'),
        },
        { value: 'bottom', label: lbl('Bottom', 'Низ', 'Dół', 'Jos', 'Abajo', 'Низ') },
        { value: 'top', label: lbl('Top', 'Верх', 'Góra', 'Sus', 'Arriba', 'Верх') },
      ],
    },
    {
      key: 'size',
      type: 'number',
      default: 1,
      min: 0.4,
      max: 2,
      step: 0.1,
      label: lbl('Size', 'Розмір', 'Rozmiar', 'Mărime', 'Tamaño', 'Размер'),
    },
    P_COLOR('bg', 'dark', lbl('Background', 'Фон', 'Tło', 'Fundal', 'Fondo', 'Фон')),
    P_COLOR('color', 'light', lbl('Text', 'Текст', 'Tekst', 'Text', 'Texto', 'Текст')),
    P_COLOR(
      'accentColor',
      'accent',
      lbl('Accent', 'Акцент', 'Akcent', 'Accent', 'Acento', 'Акцент'),
    ),
  ],
  build(ctx) {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const u = unit(ctx);
    const style = str(ctx, 'style', 'blink') as CaptionStyle;
    const overlay = style === 'pop' || style === 'karaoke';
    const chunkMode = str(ctx, 'chunk', 'auto');
    const fromDesign = str(ctx, 'source', 'all') === 'headline' ? ctx.text : ctx.copy || ctx.text;
    const script = str(ctx, 'text', fromDesign || 'Hello');
    // Hard cuts show one word at a time unless the text is long enough to turn into a strobe
    const mode =
      chunkMode === 'word' || chunkMode === 'phrase' || chunkMode === 'line'
        ? chunkMode
        : style === 'blink' && glueWords(script).length <= 10
          ? 'word'
          : 'phrase';
    const chunks = captionChunks(script, mode);
    if (!chunks.length) return [];
    const times = chunkTimes(chunks, D);
    const backdropParam = str(ctx, 'backdrop', 'auto');
    const backdrop = backdropParam === 'auto' ? (overlay ? 'dim' : 'solid') : backdropParam;
    const posParam = str(ctx, 'position', 'auto');
    const position = posParam === 'auto' ? (overlay ? 'bottom' : 'center') : posParam;
    const bg = color(ctx, 'bg', 'dark');
    // On a solid backdrop the text must read on it; over the design use light text with a shadow
    const fg =
      backdrop === 'solid'
        ? readable(bg, color(ctx, 'color', 'light'), ctx.palette.light, ctx.palette.dark)
        : color(ctx, 'color', 'light');
    // The accent must stand out from the backdrop too (dimmed design reads as the dark colour)
    const accent =
      backdrop === 'none'
        ? color(ctx, 'accentColor', 'accent')
        : readable(
            bg,
            color(ctx, 'accentColor', 'accent'),
            ctx.palette.accent2,
            shade(ctx.palette.accent, 0.5),
            fg,
          );
    const upper = ctx.font.transform === 'uppercase';
    const size = Math.max(0.4, Math.min(2, num(ctx, 'size', 1)));
    const pad = Math.min(W, H) * (overlay ? 0.07 : 0.08);
    const boxH = overlay ? H * 0.3 : H - pad * 2;
    const maxF =
      Math.min(W, H) *
      (style === 'blink' ? 0.3 : style === 'trailer' ? 0.1 : overlay ? 0.115 : 0.14) *
      size;
    const shadow = backdrop === 'solid' ? 'none' : `0 ${px(2 * u)} ${px(18 * u)} rgba(0,0,0,0.55)`;

    const nodes: FxNode[] = [];
    if (backdrop === 'solid' || backdrop === 'dim') {
      nodes.push({
        style: full(ctx, {
          background:
            backdrop === 'solid'
              ? bg
              : position === 'bottom'
                ? `linear-gradient(to top, ${withAlpha(bg, 0.75)}, ${withAlpha(bg, 0)} 55%)`
                : position === 'top'
                  ? `linear-gradient(to bottom, ${withAlpha(bg, 0.75)}, ${withAlpha(bg, 0)} 55%)`
                  : withAlpha(bg, 0.5),
        }),
      });
    }

    const area = (
      extra: Record<string, string | number> = {},
    ): Record<string, string | number> => ({
      position: 'absolute',
      left: px(pad),
      width: px(W - pad * 2),
      height: px(boxH),
      top: px(position === 'bottom' ? H - pad - boxH : position === 'top' ? pad : (H - boxH) / 2),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent:
        position === 'bottom' ? 'flex-end' : position === 'top' ? 'flex-start' : 'center',
      textAlign: 'center',
      fontFamily: ctx.font.family,
      fontWeight: ctx.font.weight,
      fontStyle: ctx.font.style,
      textTransform: ctx.font.transform,
      lineHeight: '1.06',
      // Room for glyphs that overhang the line box, so text flush with an edge still "fits"
      boxSizing: 'border-box',
      padding: `${px(Math.min(W, H) * 0.02)} 0`,
      textShadow: shadow,
      visibility: 'hidden',
      ...extra,
    });
    const rowsOf = (words: string[], f: number, children: FxNode[]): FxNode[] => {
      const rows: FxNode[] = [];
      let k = 0;
      for (const line of balancedLines(words, f, W - pad * 2, upper)) {
        rows.push({
          style: {
            display: 'flex',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          },
          children: children.slice(k, k + line.length),
        });
        k += line.length;
      }
      return rows;
    };
    const wordTimes = (words: string[], len: number) => {
      const w = words.map((x) => [...x].length + 2);
      const sum = w.reduce((a, b) => a + b, 0);
      let at = 0;
      return w.map((x) => {
        const o = { at, len: (len * x) / sum };
        at += o.len;
        return o;
      });
    };

    chunks.forEach((words, ci) => {
      const { at, len } = times[ci];
      const f = fitFont(words, W - pad * 2, boxH, upper, maxF);
      const last = ci === chunks.length - 1;
      const word = (
        w: string,
        extra: Record<string, string | number> = {},
        anims?: FxAnim[],
      ): FxNode => ({
        text: w,
        style: {
          display: 'inline-block',
          margin: `0 ${px(f * 0.13)}`,
          whiteSpace: 'pre',
          ...extra,
        },
        anims,
      });
      if (style === 'blink') {
        // Apple "Don't blink": every card is a hard cut; the key card is in the accent colour
        const key = chunks.length > 2 && last;
        nodes.push({
          style: area({ fontSize: px(f), color: key ? accent : fg, letterSpacing: '-0.02em' }),
          fit: true,
          children: rowsOf(
            words,
            f,
            words.map((w) => word(w)),
          ),
          anims: [
            visible(at, len),
            {
              delay: at,
              duration: Math.max(1, len),
              keyframes: [
                { transform: 'scale(1.08)', easing: OUT },
                { transform: 'scale(1)', offset: Math.min(1, 220 / Math.max(1, len)) },
                { transform: 'scale(1)' },
              ],
            },
          ],
        });
        return;
      }
      if (style === 'trailer') {
        const fade = Math.min(450, len * 0.3);
        nodes.push({
          style: area({ fontSize: px(f), color: fg, letterSpacing: '0.12em' }),
          fit: true,
          children: rowsOf(
            words,
            f,
            words.map((w) => word(w)),
          ),
          anims: [
            visible(at, len),
            {
              delay: at,
              duration: Math.max(1, len),
              keyframes: [
                { opacity: 0, filter: `blur(${px(12 * u)})`, letterSpacing: '0.4em', easing: OUT },
                {
                  opacity: 1,
                  filter: 'blur(0px)',
                  letterSpacing: '0.16em',
                  offset: fade / Math.max(1, len),
                },
                {
                  opacity: 1,
                  filter: 'blur(0px)',
                  letterSpacing: '0.12em',
                  offset: 1 - fade / Math.max(1, len),
                },
                { opacity: 0, filter: `blur(${px(6 * u)})`, letterSpacing: '0.1em' },
              ],
            },
          ],
        });
        return;
      }
      if (style === 'slide') {
        const inD = Math.min(450, len * 0.35);
        const outD = last ? 0 : Math.min(300, len * 0.25);
        const children = words.map((w, i) =>
          word(
            w,
            { overflow: 'hidden', verticalAlign: 'top', paddingBottom: px(f * 0.08) },
            undefined,
          ),
        );
        children.forEach((c, i) => {
          c.children = [
            {
              text: c.text,
              style: { display: 'inline-block' },
              anims: [
                {
                  delay: at,
                  duration: Math.max(1, len),
                  keyframes: [
                    { transform: 'translateY(110%)', easing: OUT },
                    {
                      transform: 'translateY(0%)',
                      offset: Math.min(1, (inD + i * 40) / Math.max(1, len)),
                    },
                    {
                      transform: 'translateY(0%)',
                      offset: outD ? 1 - outD / Math.max(1, len) : 1,
                      easing: IN,
                    },
                    { transform: outD ? 'translateY(-110%)' : 'translateY(0%)' },
                  ],
                },
              ],
            },
          ];
          delete c.text;
        });
        nodes.push({
          style: area({ fontSize: px(f), color: fg }),
          fit: true,
          children: rowsOf(words, f, children),
          anims: [visible(at, len)],
        });
        return;
      }
      // Word by word over the design: pop each word in, or highlight the word being said
      const wt = wordTimes(words, len);
      const onAccent =
        contrast(accent, ctx.palette.dark) >= contrast(accent, ctx.palette.light)
          ? ctx.palette.dark
          : ctx.palette.light;
      const children = words.map((w, i) => {
        const t0 = wt[i].at;
        if (style === 'pop') {
          return word(w, { color: fg, borderRadius: px(f * 0.18), padding: `0 ${px(f * 0.12)}` }, [
            {
              delay: at,
              duration: Math.max(1, len),
              keyframes: [
                { opacity: 0, transform: 'scale(0.4)', offset: 0 },
                {
                  opacity: 0,
                  transform: 'scale(0.4)',
                  offset: t0 / Math.max(1, len),
                  easing: BACK,
                },
                {
                  opacity: 1,
                  transform: 'scale(1)',
                  offset: Math.min(1, (t0 + 220) / Math.max(1, len)),
                },
                { opacity: 1, transform: 'scale(1)' },
              ],
            },
            {
              // The word being said sits on an accent pill
              delay: at + t0,
              duration: Math.max(1, wt[i].len),
              fill: 'none',
              keyframes: [
                {
                  background: accent,
                  color: onAccent,
                },
                {
                  background: accent,
                  color: onAccent,
                },
              ],
            },
          ]);
        }
        return word(w, { color: withAlpha(fg, 0.55) }, [
          {
            delay: at + t0,
            duration: Math.max(1, len - t0),
            fill: 'forwards',
            keyframes: [
              { color: accent, transform: 'scale(1.08)', easing: OUT },
              {
                color: accent,
                transform: 'scale(1)',
                offset: Math.min(1, 200 / Math.max(1, len - t0)),
              },
              { color: fg, transform: 'scale(1)' },
            ],
          },
        ]);
      });
      nodes.push({
        style: area({ fontSize: px(f), letterSpacing: '-0.01em' }),
        fit: true,
        children: rowsOf(words, f, children),
        anims: [visible(at, len)],
      });
    });
    return nodes;
  },
};

export const FX_KINDS: FxKind[] = [
  panels,
  diagonal,
  shutter,
  slices,
  stripes,
  doors,
  circle,
  flash,
  rings,
  burst,
  confetti,
  sweep,
  frame,
  underline,
  lines,
  glitch,
  letterbox,
  aurora,
  grid,
  shapes,
  marquee,
  titleCard,
  captions,
  ...REEL_FX_KINDS,
  ...ORIGINAL_FX_KINDS,
];
/**
 * Text effects draw with the font of a text layer chosen as their source, or a font picked for
 * them, so they match the design instead of always using the headline's font.
 */
export const FX_SOURCE_KINDS = new Set([
  'title',
  'marquee',
  'words',
  'slice-text',
  'extrude',
  'glitch-text',
  'neon',
  'word-wall',
  'text-rings',
]);
/** Effects a text layer can carry (`motion.effect`): text effects and the typing terminal. */
export const TEXT_EFFECT_KINDS = new Set([...FX_SOURCE_KINDS, 'terminal']);
export const FX_FONT_KINDS = new Set([
  ...FX_SOURCE_KINDS,
  'captions',
  'counter',
  'donut',
  'countdown',
  'sticker',
]);
for (const kind of FX_KINDS) {
  const extra = [
    // The terminal types the text of a layer in its own monospaced font
    ...(kind.id === 'terminal' ? [P_SOURCE, P_HIDE_SOURCE] : []),
    ...(FX_SOURCE_KINDS.has(kind.id) ? [P_SOURCE, P_HIDE_SOURCE] : []),
    ...(FX_FONT_KINDS.has(kind.id) ? [P_FONT, P_WEIGHT] : []),
  ];
  for (const p of extra) if (!kind.params.some((q) => q.key === p.key)) kind.params.push(p);
}

export const FX_KIND_MAP = new Map(FX_KINDS.map((k) => [k.id, k]));
export const FX_CATEGORIES: FxCategory[] = [
  'text',
  'interface',
  'pattern',
  'cover',
  'accent',
  'ambient',
];

export function fxDefaults(kind: FxKind, params?: PresetParams): PresetParams {
  const out: PresetParams = {};
  for (const p of kind.params) out[p.key] = p.default;
  return Object.assign(out, params ?? {});
}
